import { useNavigate } from 'react-router-dom';
import useCaseOwner from './useCaseOwner';
import useCertifiedLifecareCompany from './useCertifiedLifecareCompany';
import { useProductContext } from '../contexts/ProductContext';
import { useChatToggle } from '../contexts/ChatToggleContext';
import { getCaseTypeText } from '../utils/helper';
import { CASE_TYPE_MAP } from '../utils/enum';
import useMarkDemandStudioOpened from './useMarkDemandStudioOpened';
import useSuggestedEditsFeatureFlag from './useSuggestedEditsFeatureFlag';
import { useDownloadOptions } from './useDownloadOptions';
import EditIconBlue from '../assets/icons/EditIconBlue.svg?react';
import ViewIcon from '../assets/icons/ViewIcon.svg?react';
import ChatIcon from '../assets/icons/ChatIcon.svg?react';
import ArrowForwardIcon from '../assets/icons/arrow_forward.svg?react';
import TaskIcon from '../assets/icons/task.svg?react';
import useSplitDropdown from './useSplitDropdown';

const menuIconClasses = "h-action-icon w-action-icon flex-none text-primary";

const useCaseSummaryHeader = ({ caseId, caseData }) => {
    const { ownerFullName, initials } = useCaseOwner(caseData?.userId);
    const { CASE_TYPE_MAP_MT } = useProductContext();
    const isCertifiedLifecareCompany = useCertifiedLifecareCompany();
    const chatToggle = useChatToggle();
    const { isOpen, toggle } = useSplitDropdown();
    const navigate = useNavigate();
    const markDemandStudioOpened = useMarkDemandStudioOpened(caseId);
    const isSuggestedEditsUiEnabled = useSuggestedEditsFeatureFlag();
    const { downloadSingleFile } = useDownloadOptions();

    const isDraftCase = caseData?.isDraftCase;
    const auditReportS3UrlPath = caseData?.docxFiles?.Case_Audit?.s3UrlPath;
    const caseName = caseData?.caseInfo?.caseName;
    const editCaseLabel = isCertifiedLifecareCompany ? "View Case" : "Edit Case";
    const editCaseIcon = isCertifiedLifecareCompany
        ? <ViewIcon className={menuIconClasses} />
        : <EditIconBlue className={menuIconClasses} />;

    const caseTypeText = getCaseTypeText({
        caseType: caseData?.caseInfo?.caseType,
        caseTypeMap: CASE_TYPE_MAP,
        caseTypeMapMT: CASE_TYPE_MAP_MT,
    });

    const menuItems = [
        isSuggestedEditsUiEnabled && {
            label: isCertifiedLifecareCompany ? "Lifecare Studio" : "Demand Studio",
            icon: <ArrowForwardIcon className={menuIconClasses} />,
            action: () => { markDemandStudioOpened(); navigate(`/case/${caseId}/${isCertifiedLifecareCompany ? "lifecare-studio" : "demand-studio"}`); },
            disabled: isDraftCase,
        },
        { label: editCaseLabel, icon: editCaseIcon, action: () => navigate(`/case/edit/${caseId}`) },
        chatToggle && {
            label: "AI Chat",
            icon: <ChatIcon className={menuIconClasses} />,
            action: () => chatToggle.setIsChatOpen((prev) => !prev),
        },
        {
            label: "Audit Case",
            icon: <TaskIcon className={menuIconClasses} />,
            action: () => downloadSingleFile({ s3UrlPath: auditReportS3UrlPath, name: `${caseName}-case-audit.pdf` }),
            disabled: !auditReportS3UrlPath,
        },
    ].filter(Boolean);

    return { ownerFullName, initials, menuItems, isOpen, toggle, caseTypeText };
};

export default useCaseSummaryHeader;
